import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { createAnimation, animations, animationKeyframes } from "./animations";
import QRScanner from "./QRScanner";

const FullScreenValidProduct = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0);
  backdrop-filter: blur(0);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${createAnimation(animations.verificationBox)};
  animation-delay: ${animations.verificationBox.delay};
  opacity: 0;
  font-family: "Montserrat", sans-serif;
  user-select: none;
`;

const SplitContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  user-select: none;
`;

const LeftSection = styled.div`
  width: 50%;
  background: linear-gradient(to bottom, #304da1, #41a4d9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: white;
  position: relative;
  opacity: 0;
  animation: ${createAnimation(animations.leftSection)};
  animation-delay: ${animations.leftSection.delay};
  box-sizing: border-box;
  user-select: none;
`;

const RightSection = styled.div`
  width: 50%;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 4rem 4rem;
  position: relative;
  box-sizing: border-box;
  user-select: none;
`;

const RightContent = styled.div`
  opacity: 0;
  animation: ${(props) =>
    props.$isVisible ? createAnimation(animations.rightContent) : "none"};
  animation-delay: ${animations.rightContent.delay};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  align-items: center;
  position: relative;
  left: 0;
  transform-origin: left center;
  user-select: none;

  img {
    width: 280px;
    margin-bottom: 0.13rem;
    filter: brightness(0) invert(1);
    position: relative;
    left: 10px;
    user-select: none;
    pointer-events: none;
  }

  .subtitle {
    font-size: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    line-height: 1.5;
    font-weight: 700;
    color: white;
    text-align: center;
    width: 300px;
    margin: 0.89rem auto 0;
  }

  .number-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0rem;
  }

  .line {
    height: 2px;
    background-color: white;
    flex: 1;
    max-width: 78px;
  }

  .number {
    margin: 0 1rem;
    font-size: 1.8rem;
    font-weight: 700;
  }
`;

const AuthenticIcon = styled.div`
  position: relative;
  margin-bottom: 2rem;
  user-select: none;

  img {
    width: 78%;
    height: auto;
    user-select: none;
    pointer-events: none;
  }
`;

const VerificationText = styled.div`
  margin-bottom: 3rem;
  text-align: center;
  opacity: 0;
  animation: ${(props) =>
    props.$isVisible ? createAnimation(animations.mainMessage) : "none"};
  animation-delay: ${animations.mainMessage.delay};
  user-select: none;

  h2 {
    font-size: 1.78rem;
    color: #333;
    margin-bottom: 0;
    font-weight: 400;
    line-height: 1.3;
    text-align: center;

    .highlight {
      color: #4caf50;
      font-weight: 700;
    }
  }

  .thank-you {
    color: #999;
    font-size: 1.1rem;
    font-style: normal;
    text-align: center;
    margin-top: 2rem;
    font-weight: 600;
    opacity: 0;
    animation: ${(props) =>
      props.$isVisible ? createAnimation(animations.thankYouMessage) : "none"};
    animation-delay: ${animations.thankYouMessage.delay};
  }
`;

const ProductDetails = styled.div`
  width: 100%;
  margin: 1.3rem 0 1.3rem auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  animation: ${(props) =>
    props.$isVisible ? createAnimation(animations.productDetails) : "none"};
  animation-delay: ${animations.productDetails.delay};
  user-select: none;

  .detail-item {
    margin-bottom: 1rem;
    margin-left: 1rem;
    text-align: left;
    width: 100%;
    max-width: 400px;
    font-weight: 700;

    &.product {
      font-size: 1.4rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 1.5rem;
    }

    &.info {
      font-size: 1.26rem;
      color: #666;
      font-weight: 500;
    }
  }
`;

const StyledButton = styled.a`
  display: inline-flex;
  align-items: center;
  background: #304da1;
  color: white;
  border-radius: 50px;
  padding: 0.875rem 2rem;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  margin-top: -1rem;
  gap: 0.75rem;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    animation: ${animationKeyframes.buttonGlowPulse} 3s ease-in-out infinite;
  }

  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
    position: relative;
    padding-left: 0.75rem;
    border-left: 2.6px solid rgba(255, 255, 255, 0.89);
    user-select: none;
    pointer-events: none;
  }
`;

const UKEntity = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 0;
  right: 0;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
  line-height: 1.5;
  user-select: none;
  display: ${(props) =>
    process.env.REACT_APP_UK_RESPONSIBLE_ENTITY === "true" &&
    props.customerId !== "0000000"
      ? "block"
      : "none"};
`;

const MaterialIconLink = styled.a`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  color: #304da1;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 1000;
  cursor: pointer;

  .material-symbols-outlined {
    font-size: 1.5rem;
    transition: color 0.3s ease;
    font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
  }

  &:hover {
    .material-symbols-outlined {
      color: #41a4d9;
    }
  }
`;

const ValidProductDisplay = ({ verificationResult }) => {
  const [showContent, setShowContent] = useState(false);
  const [productData, setProductData] = useState(null);
  const [showValidProduct, setShowValidProduct] = useState(false);

  const handleQRAnimationComplete = () => {
    setShowContent(true);
  };

  const handleWebsiteClick = (e) => {
    e.preventDefault();
    window.location.href = "https://barjuicevape.com";
  };

  // Format icon name for Material Icons
  const formatIconName = (name) => {
    if (!name) return "info";
    return name.replace(/\s+/g, "_");
  };

  useEffect(() => {
    if (verificationResult) {
      setProductData(verificationResult.data);
      setShowValidProduct(true);
    }
  }, [verificationResult]);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200"
      />
      <FullScreenValidProduct>
        <SplitContainer>
          <LeftSection>
            <Logo>
              <img src="/images/BJ-logo.png" alt="Bar Juice" />
              <div className="number-container">
                <div className="line"></div>
                <div className="number">5000</div>
                <div className="line"></div>
              </div>
              <div className="subtitle">REFILLABLE E-LIQUID</div>
            </Logo>
          </LeftSection>
          <RightSection>
            <RightContent $isVisible={true}>
              {process.env.REACT_APP_MATERIAL_ICON_HIDE !== "true" && (
                <MaterialIconLink
                  href={`${
                    process.env.REACT_APP_MATERIAL_ICON_URL ||
                    "https://example.com"
                  }${verificationResult?.data?.code || "test"}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="material-symbols-outlined">
                    {formatIconName(process.env.REACT_APP_MATERIAL_ICON_NAME)}
                  </span>
                </MaterialIconLink>
              )}
              <AuthenticIcon>
                <img src="/images/authentic-2.png" alt="Verified" />
              </AuthenticIcon>
              <VerificationText $isVisible={showContent}>
                <h2>
                  Your product is <span className="highlight">authentic</span>{" "}
                  and has
                  <br />
                  passed our verification system.
                </h2>
                <div className="thank-you">
                  Thank you for choosing Bar Juice.
                </div>
              </VerificationText>
              <ProductDetails $isVisible={showContent}>
                <div className="detail-item product">
                  Product:{" "}
                  {verificationResult.data.productBrand &&
                  verificationResult.data.productName
                    ? `${verificationResult.data.productBrand} ${verificationResult.data.productName}`
                    : "ZOHO ITEM NAME"}
                </div>
                <div className="detail-item info">
                  First Scanned:{" "}
                  {verificationResult.data.firstScanned
                    ? new Date(
                        verificationResult.data.firstScanned
                      ).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : new Date().toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                </div>
                <div className="detail-item info">
                  Number of Scans: {verificationResult.data.scanCount}
                </div>
                <div className="detail-item info">
                  Code: {verificationResult.data.code || "xxxxxxxxxxxxx"}
                </div>
              </ProductDetails>
              <StyledButton
                href="https://barjuicevape.com"
                onClick={handleWebsiteClick}
              >
                go to barjuicevape.com
                <img src="/icons/icon-network.svg" alt="Visit Website" />
              </StyledButton>
              {process.env.REACT_APP_UK_RESPONSIBLE_ENTITY === "true" &&
                verificationResult.data.customerId && (
                  <UKEntity>
                    UK Responsible Entity:
                    <br />
                    {verificationResult.data.customerId}
                  </UKEntity>
                )}
            </RightContent>
          </RightSection>
        </SplitContainer>
        <QRScanner
          onAnimationComplete={handleQRAnimationComplete}
          isNetworkError={false}
        />
      </FullScreenValidProduct>
    </>
  );
};

export default ValidProductDisplay;
